// @ts-ignore - TypeScript has issues with Pinia imports, but they work at runtime
import { defineStore } from 'pinia';
import type { Ref } from 'vue';
import { ref } from 'vue';
import menuService from '@/services/menuService';
import type { MenuItem, MenuCategory } from '@/types/menu';

export const useMenuStore = defineStore('menu', () => {
  const menuItems: Ref<MenuItem[]> = ref<MenuItem[]>([]);
  // Detailed categories with IDs
  const categoriesDetailed: Ref<MenuCategory[]> = ref<MenuCategory[]>([]);
  // Backwards compatible list of category names used by existing forms/components
  const categories: Ref<string[]> = ref<string[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  type StoreError = {
    response?: { data?: { message?: string; detail?: string } };
    message?: string;
  };

  const isSameId = (a: string | number | undefined, b: string | number | undefined) => {
    if (a === undefined || b === undefined) return false;
    return String(a) === String(b);
  };

  const toNumber = (value: unknown, fallback = 0): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const getErrorMessage = (err: unknown, fallback: string): string => {
    const typedError = err as StoreError;
    return typedError.response?.data?.message || typedError.response?.data?.detail || typedError.message || fallback;
  };

  const syncCategoriesState = (nextCategories: MenuCategory[]) => {
    categoriesDetailed.value = nextCategories;
    categories.value = nextCategories.map((category) => category.name);
  };

  const upsertMenuItemState = (item: MenuItem, matchId?: string | number) => {
    const idToMatch = matchId ?? item.id;
    const index = menuItems.value.findIndex((current) => isSameId(current.id, idToMatch));

    if (index !== -1) {
      menuItems.value[index] = item;
      return;
    }

    menuItems.value.push(item);
  };

  const formatVariantPayload = (variants?: MenuItem['variants'], includeId = false) => {
    if (!Array.isArray(variants)) return [];

    return variants.map((variant) => ({
      ...(includeId && variant.id ? { id: variant.id } : {}),
      name: variant.name,
      price: toNumber(variant.price),
      discount_price: variant.discount_price ? toNumber(variant.discount_price) : undefined,
      is_available: variant.is_available ?? true,
    }));
  };

  async function withLoading<T>(
    action: () => Promise<T>,
    fallbackErrorMessage: string,
    options?: { throwOriginal?: boolean; returnOnError?: T }
  ): Promise<T> {
    loading.value = true;
    error.value = null;
    try {
      return await action();
    } catch (err: unknown) {
      const message = getErrorMessage(err, fallbackErrorMessage);
      error.value = message;

      if (options && 'returnOnError' in options) {
        return options.returnOnError as T;
      }

      if (options?.throwOriginal) {
        throw err;
      }

      throw new Error(message);
    } finally {
      loading.value = false;
    }
  }

  // Helper to normalize menu item data
  function normalizeMenuItem(item: any): MenuItem {
    // Preserve category as MenuCategory object if it has id
    let category: MenuCategory | undefined;
    
    if (item.category && typeof item.category === 'object' && 'id' in item.category) {
      // Preserve the full category object with id
      category = {
        id: item.category.id,
        name: item.category.name || '',
        description: item.category.description,
        visible_in_kitchen: item.category.visible_in_kitchen
      };
    } else if (typeof item.category === 'string' && item.category) {
      // If it's just a string, create a minimal category object
      category = {
        id: 0,
        name: item.category
      };
    }
      
    return {
      id: item.id,
      name: item.name,
      description: item.description || '',
      price: toNumber(item.price),
      discount_price: item.discount_price ? toNumber(item.discount_price) : undefined,
      category: category,
      is_available: item.is_available ?? true,
      image_url: item.image_url ?? '',
      modifier_groups: Array.isArray(item.modifier_groups) ? item.modifier_groups : [],
      variants: Array.isArray(item.variants) 
        ? item.variants.map((v: any) => ({
            id: v.id,
            name: v.name || '',
            price: toNumber(v.price),
            discount_price: v.discount_price ? toNumber(v.discount_price) : undefined,
            is_available: v.is_available ?? true
          }))
        : [],
      created_at: item.created_at,
      updated_at: item.updated_at
    };
  }

  async function fetchMenuItems(categoryId?: number, available?: boolean, search?: string) {
    return withLoading(async () => {
      const response = await menuService.getMenuItems(categoryId, available, search);
      menuItems.value = Array.isArray(response) ? response.map(normalizeMenuItem) : [];
      return menuItems.value;
    }, 'Failed to fetch menu items');
  }

  async function getMenuItem(id: string | number) {
    return withLoading(async () => {
      const response = await menuService.getMenuItem(id);
      return normalizeMenuItem(response);
    }, `Failed to fetch menu item ${id}`);
  }

  async function createMenuItem(menuItemData: Omit<MenuItem, 'id'>) {
    return withLoading(async () => {
      // Prepare the data with proper variant format
      const menuItemPayload = {
        ...menuItemData,
        variants: formatVariantPayload(menuItemData.variants)
      };
      
      const response = await menuService.createMenuItem(menuItemPayload);
      const newItem = normalizeMenuItem(response);
      upsertMenuItemState(newItem);
      return newItem;
    }, 'Failed to create menu item', { throwOriginal: true });
  }

  async function updateMenuItem(id: string | number, menuItemData: Partial<MenuItem>) {
    return withLoading(async () => {
      // Prepare the data with proper variant format
      const menuItemPayload: Partial<MenuItem> = { ...menuItemData };
      
      // Only include variants if they are provided in the update
      if (menuItemData.variants) {
        menuItemPayload.variants = formatVariantPayload(menuItemData.variants, true);
      }
      
      const response = await menuService.updateMenuItem(id, menuItemPayload);
      const normalizedItem = normalizeMenuItem(response);
      upsertMenuItemState(normalizedItem, id);
      return normalizedItem;
    }, `Failed to update menu item ${id}`, { throwOriginal: true });
  }

  async function deleteMenuItem(id: string | number) {
    return withLoading(async () => {
      await menuService.deleteMenuItem(id);
      const index = menuItems.value.findIndex((item) => isSameId(item.id, id));
      if (index !== -1) {
        menuItems.value.splice(index, 1);
      }
      return true;
    }, 'Failed to delete menu item', { throwOriginal: true });
  }

  async function toggleMenuItemAvailability(id: string | number) {
    const item = menuItems.value.find(item => item.id === id);
    if (!item) return;

    return withLoading(async () => {
      const response = await menuService.updateMenuItemAvailability(id, !item.is_available);
      const updatedItem = normalizeMenuItem(response);
      upsertMenuItemState(updatedItem, id);
      return updatedItem;
    }, 'Failed to update menu item availability', { throwOriginal: true });
  }

  // Get all menu categories
  async function getCategories(forceRefresh = false, search?: string) {
    const hasSearch = !!search?.trim();

    // Return cached categories if we have them and don't force refresh
    if (!hasSearch && !forceRefresh && categories.value.length > 0) {
      return categories.value;
    }
    
    return withLoading(async () => {
      const categoryData = await menuService.getCategories(search);
      const normalizedCategories = Array.isArray(categoryData) ? categoryData : [];
      syncCategoriesState(normalizedCategories);
      return categories.value;
    }, 'Failed to fetch categories', { returnOnError: [] });
  }

  async function createCategory(name: string, description?: string, visibleInKitchen = true) {
    return withLoading(async () => {
      const created = await menuService.createCategory({
        name,
        description,
        visible_in_kitchen: visibleInKitchen,
      });
      syncCategoriesState([...categoriesDetailed.value, created]);
      return created;
    }, 'Failed to create category');
  }

  async function updateCategory(id: string | number, data: { name?: string; description?: string; visible_in_kitchen?: boolean }) {
    return withLoading(async () => {
      const updated = await menuService.updateCategory(id, data);
      const nextCategories = categoriesDetailed.value.map((category) =>
        isSameId(category.id, id) ? updated : category
      );
      syncCategoriesState(nextCategories);
      return updated;
    }, 'Failed to update category');
  }

  async function deleteCategory(id: string | number) {
    return withLoading(async () => {
      await menuService.deleteCategory(id);
      const nextCategories = categoriesDetailed.value.filter((category) => !isSameId(category.id, id));
      syncCategoriesState(nextCategories);
      return true;
    }, 'Failed to delete category');
  }

  return {
    menuItems,
    categoriesDetailed,
    categories,
    loading,
    error,
    fetchMenuItems,
    getMenuItem,
    createMenuItem,
    updateMenuItem,
    deleteMenuItem,
    toggleMenuItemAvailability,
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  };
});
